import { useEffect, useRef, useState } from 'react';
import { CalendarDays, ImagePlus, Images, LayoutGrid, Mail, Megaphone, Plus } from 'lucide-react';
import { FacebookIcon, GoogleBusinessIcon, InstagramIcon, LinkedInIcon } from '../PlatformIcon';

export type MarketingNavItem = 'plan' | 'library' | 'platforms' | 'templates';

export type MarketingCreateAction = 'campaign' | 'gbp' | 'facebook' | 'instagram' | 'linkedin' | 'upload';

interface MarketingBottomNavProps {
  /** Which bottom item should render as active (null = none). */
  active: MarketingNavItem | null;
  /** Which post types the [+] menu should offer (connected platforms only). */
  createChannels: { gbp: boolean; facebook: boolean; instagram: boolean; linkedin: boolean };
  onPlan: () => void;
  onLibrary: () => void;
  onCreateAction: (action: MarketingCreateAction) => void;
  onPlatforms: () => void;
  onTemplates: () => void;
}

/**
 * Bottom tab bar for the Marketing home — five equal tiles:
 * Plan (calendar) · Library · Create · Platforms · Email templates.
 * Create is deliberately a plain tile (not a raised circle) so the bar reads
 * as page tabs and doesn't echo the app's own floating action bar on iOS.
 */
export default function MarketingBottomNav({
  active,
  createChannels,
  onPlan,
  onLibrary,
  onCreateAction,
  onPlatforms,
  onTemplates,
}: MarketingBottomNavProps) {
  const [createOpen, setCreateOpen] = useState(false);
  const createRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!createOpen) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      const t = e.target as Node;
      if (createRef.current && !createRef.current.contains(t)) setCreateOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [createOpen]);

  const createItems: { id: MarketingCreateAction; label: string; icon: typeof Plus | typeof GoogleBusinessIcon }[] = [
    { id: 'campaign', label: 'Campaign', icon: Megaphone },
    ...(createChannels.gbp
      ? [{ id: 'gbp' as const, label: 'Google Business post', icon: GoogleBusinessIcon }]
      : []),
    ...(createChannels.facebook
      ? [{ id: 'facebook' as const, label: 'Facebook post', icon: FacebookIcon }]
      : []),
    ...(createChannels.instagram
      ? [{ id: 'instagram' as const, label: 'Instagram post', icon: InstagramIcon }]
      : []),
    ...(createChannels.linkedin
      ? [{ id: 'linkedin' as const, label: 'LinkedIn post', icon: LinkedInIcon }]
      : []),
    { id: 'upload', label: 'Upload media', icon: ImagePlus },
  ];

  const itemClass = (isActive: boolean) =>
    `flex flex-1 flex-col items-center justify-center gap-0.5 min-w-0 py-1.5 rounded-xl touch-manipulation transition-colors ${
      isActive ? 'text-gray-900' : 'text-gray-400 hover:text-gray-600 active:text-gray-700'
    }`;

  const labelClass = (isActive: boolean) =>
    `text-[11px] leading-none ${isActive ? 'font-semibold' : 'font-medium'}`;

  return (
    <div
      className="shrink-0 border-t border-gray-200/70 bg-white/95 backdrop-blur-sm z-20"
      style={{
        WebkitBackdropFilter: 'blur(12px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", system-ui, sans-serif',
      }}
    >
      <div className="mx-auto flex w-full max-w-2xl items-stretch justify-between px-2 sm:px-6 py-1.5">
        <button
          type="button"
          onClick={onPlan}
          className={itemClass(active === 'plan')}
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          <CalendarDays className="h-[22px] w-[22px]" strokeWidth={active === 'plan' ? 2.2 : 1.9} />
          <span className={labelClass(active === 'plan')}>Plan</span>
          <span
            className={`h-[3px] w-5 rounded-full ${active === 'plan' ? 'bg-gray-900' : 'bg-transparent'}`}
            aria-hidden
          />
        </button>

        <button
          type="button"
          onClick={onLibrary}
          className={itemClass(active === 'library')}
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          <Images className="h-[22px] w-[22px]" strokeWidth={active === 'library' ? 2.2 : 1.9} />
          <span className={labelClass(active === 'library')}>Library</span>
          <span
            className={`h-[3px] w-5 rounded-full ${active === 'library' ? 'bg-gray-900' : 'bg-transparent'}`}
            aria-hidden
          />
        </button>

        <div className="relative flex flex-1 min-w-0" ref={createRef}>
          <button
            type="button"
            onClick={() => setCreateOpen((v) => !v)}
            aria-label="Create"
            aria-expanded={createOpen}
            className={itemClass(createOpen)}
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <span
              className={`flex h-[22px] w-[22px] items-center justify-center rounded-[6px] border-[1.9px] ${
                createOpen ? 'border-gray-900 bg-gray-900 text-white' : 'border-current'
              }`}
            >
              <Plus
                className={`h-3.5 w-3.5 transition-transform ${createOpen ? 'rotate-45' : ''}`}
                strokeWidth={2.4}
              />
            </span>
            <span className={labelClass(createOpen)}>Create</span>
            <span
              className={`h-[3px] w-5 rounded-full ${createOpen ? 'bg-gray-900' : 'bg-transparent'}`}
              aria-hidden
            />
          </button>

          {createOpen ? (
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 w-60 rounded-2xl border border-gray-200 bg-white shadow-xl overflow-hidden py-1">
              {createItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => {
                      setCreateOpen(false);
                      onCreateAction(item.id);
                    }}
                    className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-[14px] font-medium text-gray-800 hover:bg-gray-50 active:bg-gray-100 touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent', letterSpacing: '-0.01em' }}
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-100 overflow-hidden">
                      <Icon className={item.id === 'gbp' || item.id === 'facebook' || item.id === 'instagram' || item.id === 'linkedin' ? 'h-8 w-8' : 'h-4 w-4 text-gray-700'} />
                    </span>
                    {item.label}
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>

        <button
          type="button"
          onClick={onPlatforms}
          className={itemClass(active === 'platforms')}
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          <LayoutGrid
            className="h-[22px] w-[22px]"
            strokeWidth={active === 'platforms' ? 2.2 : 1.9}
          />
          <span className={labelClass(active === 'platforms')}>Platforms</span>
          <span
            className={`h-[3px] w-5 rounded-full ${active === 'platforms' ? 'bg-gray-900' : 'bg-transparent'}`}
            aria-hidden
          />
        </button>

        <button
          type="button"
          onClick={onTemplates}
          className={itemClass(active === 'templates')}
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          <Mail className="h-[22px] w-[22px]" strokeWidth={active === 'templates' ? 2.2 : 1.9} />
          <span className={`${labelClass(active === 'templates')} whitespace-nowrap`}>
            {/* Five tiles on a 390px phone leave ~75px each — the full label needs ~85px. */}
            <span className="sm:hidden">Templates</span>
            <span className="hidden sm:inline">Email templates</span>
          </span>
          <span
            className={`h-[3px] w-5 rounded-full ${active === 'templates' ? 'bg-gray-900' : 'bg-transparent'}`}
            aria-hidden
          />
        </button>
      </div>
    </div>
  );
}
